/* =========================================================
   GHARYARD ORDER TRACKING
   Premium Storefront Controller — Version 10
   Secure lookup + timeline + latest update + products
   ========================================================= */

(() => {
  "use strict";

  const root = document.querySelector("[data-gharyard-order-tracking]");
  if (!root) return;

  const endpoint =
    root.dataset.endpoint ||
    "https://gharyard-viora-ai.wispy-block-5506.workers.dev/order-track";

  const whatsappNumber = String(root.dataset.whatsapp || "916290250125")
    .replace(/\D/g, "");

  const form = root.querySelector(".got-form");
  const orderInput = root.querySelector(".got-order-input");
  const contactInput = root.querySelector(".got-contact-input");
  const submitButton = root.querySelector(".got-submit");
  const submitText = submitButton?.querySelector("span:nth-of-type(1)");
  const errorBox = root.querySelector(".got-form-error");
  const result = root.querySelector(".got-result");
  const resultArea = root.querySelector(".got-result-area");
  const searchCard = root.querySelector(".got-search-card");

  const orderNumber = root.querySelector(".got-order-number");
  const statusPill = root.querySelector(".got-status-pill");
  const orderedDate = root.querySelector(".got-ordered-date");
  const estimatedDate = root.querySelector(".got-estimated-date");
  const lastUpdated = root.querySelector(".got-last-updated");

  const timelineProgress = root.querySelector(".got-timeline-progress");
  const timelineSteps = [...root.querySelectorAll(".got-step")];

  const latestStatus = root.querySelector(".got-latest-status");
  const latestTime = root.querySelector(".got-latest-time");
  const latestMessage = root.querySelector(".got-latest-message");

  const courier = root.querySelector(".got-courier");
  const awb = root.querySelector(".got-awb");
  const currentStatusEl = root.querySelector(".got-current-status");
  const lastUpdateEl = root.querySelector(".got-last-update");
  const copyButton = root.querySelector(".got-copy");
  const courierLink = root.querySelector(".got-courier-link");

  const productsCard = root.querySelector(".got-products-card");
  const productsList = root.querySelector(".got-products-list");
  const productsCount = root.querySelector(".got-products-count");

  const trackAnotherButton = root.querySelector(".got-track-another");
  const whatsappLinks = [...root.querySelectorAll(".got-whatsapp-link")];

  const REQUEST_TIMEOUT = 25000;

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function canAutoFocus() {
    return window.matchMedia("(min-width: 750px) and (pointer: fine)").matches;
  }

  function setWhatsAppLinks(orderName = "") {
    const message = orderName
      ? `Hi Gharyard Support, I need help with order ${orderName}.`
      : "Hi Gharyard Support, I need help with my order.";

    const url =
      `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    whatsappLinks.forEach(link => {
      link.href = url;
    });
  }

  setWhatsAppLinks();

  function normalizeText(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ");
  }

  function normalizeCourierName(value) {
    const raw = normalizeText(value);
    if (!raw) return "Not available yet";

    return raw
      .replace(/New\d+gm/gi, "")
      .replace(/[_-]+/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim() || raw;
  }

  function toDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatDate(value) {
    const date = toDate(value);
    if (!date) return "";

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).format(date);
  }

  function formatDateTime(value) {
    const date = toDate(value);
    if (!date) return "";

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(date);
  }

  function titleCaseStatus(value) {
    return normalizeText(value)
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  function eventArray(shipment) {
    if (!shipment) return [];
    if (Array.isArray(shipment.events)) return shipment.events;
    if (Array.isArray(shipment.events?.nodes)) return shipment.events.nodes;
    if (Array.isArray(shipment.events?.edges)) {
      return shipment.events.edges.map(edge => edge?.node).filter(Boolean);
    }
    return [];
  }

  function normalizeStatusCode(value) {
    return normalizeText(value)
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
  }

  function normalizedShipmentStatus(shipment, order) {
    if (order?.cancelled) return "Cancelled";

    if (shipment?.deliveredAt) return "Delivered";

    const candidates = [
      shipment?.latestEvent?.status,
      shipment?.displayStatus,
      shipment?.statusCode,
      shipment?.status,
      order?.fulfillmentStatus
    ];

    for (const candidate of candidates) {
      const code = normalizeStatusCode(candidate);
      if (!code) continue;

      if (code.includes("DELIVERED")) return "Delivered";
      if (
        code.includes("OUT_FOR_DELIVERY") ||
        code.includes("OUT_FOR_DELIVERY")
      ) return "Out for Delivery";
      if (
        code.includes("IN_TRANSIT") ||
        code.includes("TRANSIT")
      ) return "In Transit";
      if (
        code.includes("SHIPPED") ||
        code.includes("FULFILLED") ||
        code.includes("SUCCESS")
      ) return "Shipped";
      if (
        code.includes("CONFIRMED") ||
        code.includes("UNFULFILLED") ||
        code.includes("OPEN")
      ) return "Confirmed";
    }

    if (order?.shipped) return "Shipped";
    return "Confirmed";
  }

  function stageIndex(status) {
    switch (status) {
      case "Delivered":
        return 4;
      case "Out for Delivery":
        return 3;
      case "In Transit":
        return 2;
      case "Shipped":
        return 1;
      case "Confirmed":
      default:
        return 0;
    }
  }

  function eventMatchesStage(event, stage) {
    const code = normalizeStatusCode(event?.status);

    if (stage === "delivered") return code.includes("DELIVERED");
    if (stage === "out_for_delivery") return code.includes("OUT_FOR_DELIVERY");
    if (stage === "in_transit") return code.includes("IN_TRANSIT") || code === "TRANSIT";
    if (stage === "shipped") {
      return code.includes("SHIPPED") || code.includes("FULFILLED");
    }
    return false;
  }

  function eventDateForStage(shipment, stage) {
    if (!shipment) return "";

    if (stage === "shipped" && shipment.shippedAt) {
      return shipment.shippedAt;
    }

    if (stage === "in_transit" && shipment.inTransitAt) {
      return shipment.inTransitAt;
    }

    if (stage === "delivered" && shipment.deliveredAt) {
      return shipment.deliveredAt;
    }

    const matchingEvents = eventArray(shipment)
      .filter(event => eventMatchesStage(event, stage))
      .sort((a, b) => {
        return (toDate(b?.happenedAt)?.getTime() || 0) -
          (toDate(a?.happenedAt)?.getTime() || 0);
      });

    return matchingEvents[0]?.happenedAt || "";
  }

  function newestShipmentEvent(shipment) {
    const candidates = [];

    if (shipment?.latestEvent) {
      candidates.push(shipment.latestEvent);
    }

    eventArray(shipment).forEach(event => candidates.push(event));

    candidates.sort((a, b) => {
      const aTime = toDate(a?.happenedAt)?.getTime() || 0;
      const bTime = toDate(b?.happenedAt)?.getTime() || 0;
      return bTime - aTime;
    });

    return candidates[0] || null;
  }

  function bestShipment(order) {
    const shipments = Array.isArray(order?.shipments)
      ? order.shipments
      : [];

    return (
      shipments.find(item => item?.trackingNumber || item?.trackingUrl) ||
      shipments[0] ||
      null
    );
  }

  function latestTimestamp(order, shipment, latestEvent) {
    const values = [
      latestEvent?.happenedAt,
      shipment?.updatedAt,
      shipment?.deliveredAt,
      shipment?.inTransitAt,
      shipment?.shippedAt,
      order?.updatedAt,
      order?.createdAt
    ];

    const dates = values
      .map(value => toDate(value))
      .filter(Boolean)
      .sort((a, b) => b.getTime() - a.getTime());

    return dates[0]?.toISOString() || "";
  }

  function fallbackLatestMessage(status, courierName) {
    switch (status) {
      case "Delivered":
        return "Your order has been marked as delivered.";
      case "Out for Delivery":
        return "Your shipment is out for delivery.";
      case "In Transit":
        return "Your shipment is moving through the delivery network.";
      case "Shipped":
        return courierName && courierName !== "Not available yet"
          ? `Your shipment has been handed over to ${courierName}.`
          : "Your shipment has been dispatched and is on its way.";
      case "Cancelled":
        return "This order has been cancelled.";
      default:
        return "Your order is confirmed. Tracking details will appear after dispatch.";
    }
  }

  function setStatusPill(status) {
    if (!statusPill) return;

    statusPill.textContent = status;
    statusPill.classList.remove(
      "is-confirmed",
      "is-cancelled",
      "is-delivered"
    );

    if (status === "Confirmed") {
      statusPill.classList.add("is-confirmed");
    } else if (status === "Cancelled") {
      statusPill.classList.add("is-cancelled");
    } else if (status === "Delivered") {
      statusPill.classList.add("is-delivered");
    }
  }

  function setTimeline(order, shipment, status) {
    const index = status === "Cancelled" ? 0 : stageIndex(status);
    const stages = [
      "confirmed",
      "shipped",
      "in_transit",
      "out_for_delivery",
      "delivered"
    ];

    timelineSteps.forEach((step, stepIndex) => {
      step.classList.toggle("is-active", stepIndex <= index);

      const dateEl = step.querySelector(".got-step-date");
      if (!dateEl) return;

      let dateValue = "";

      if (stages[stepIndex] === "confirmed") {
        dateValue = order?.createdAt || "";
      } else {
        dateValue = eventDateForStage(shipment, stages[stepIndex]);
      }

      dateEl.textContent = formatDate(dateValue);
    });

    if (timelineProgress) {
      timelineProgress.style.width =
        `${Math.max(0, Math.min(100, (index / 4) * 100))}%`;
    }
  }

  function setLoading(isLoading) {
    if (!submitButton) return;

    submitButton.disabled = isLoading;

    if (submitText) {
      submitText.textContent = isLoading
        ? "Checking order…"
        : "Track Order";
    }
  }

  function showError(message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.classList.add("is-visible");
  }

  function clearError() {
    if (!errorBox) return;
    errorBox.textContent = "";
    errorBox.classList.remove("is-visible");
  }

  function showResult() {
    result?.classList.add("is-visible");

    window.setTimeout(() => {
      resultArea?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "start"
      });
    }, 100);
  }

  function hideResult() {
    result?.classList.remove("is-visible");
  }

  function setCourierLink(url, courierName) {
    if (!courierLink) return;

    if (!url) {
      courierLink.classList.remove("is-visible");
      courierLink.removeAttribute("href");
      return;
    }

    courierLink.href = url;
    courierLink.textContent =
      courierName && courierName !== "Not available yet"
        ? `↗ Track on ${courierName} Website`
        : "↗ Track on Courier Website";

    courierLink.classList.add("is-visible");
  }

  function setCopyButton(value) {
    if (!copyButton) return;

    if (!value) {
      copyButton.hidden = true;
      copyButton.dataset.copyValue = "";
      return;
    }

    copyButton.hidden = false;
    copyButton.dataset.copyValue = value;
    copyButton.setAttribute("aria-label", `Copy tracking number ${value}`);
  }

  async function copyTrackingNumber() {
    const value = copyButton?.dataset.copyValue;
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      const originalLabel = copyButton.getAttribute("aria-label");
      copyButton.setAttribute("aria-label", "Copied");
      copyButton.classList.add("is-copied");

      window.setTimeout(() => {
        copyButton.classList.remove("is-copied");
        if (originalLabel) {
          copyButton.setAttribute("aria-label", originalLabel);
        }
      }, 1400);
    } catch {
      // Clipboard may be blocked in some embedded browsers.
    }
  }

  copyButton?.addEventListener("click", copyTrackingNumber);

  function lineItemArray(order) {
    const raw =
      order?.items ??
      order?.lineItems ??
      order?.products ??
      [];

    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.nodes)) return raw.nodes;
    if (Array.isArray(raw?.edges)) {
      return raw.edges.map(edge => edge?.node).filter(Boolean);
    }

    return [];
  }

  function moneyValue(item) {
    const candidates = [
      item?.price,
      item?.priceAmount,
      item?.discountedUnitPriceSet?.shopMoney,
      item?.originalUnitPriceSet?.shopMoney,
      item?.variant?.price
    ];

    for (const value of candidates) {
      if (value === null || value === undefined || value === "") continue;

      if (typeof value === "object") {
        const amount = value.amount;
        if (amount !== undefined && amount !== null) {
          return {
            amount: String(amount),
            currency: value.currencyCode || orderCurrency(item)
          };
        }
      }

      if (typeof value === "number" || /^\d+(\.\d+)?$/.test(String(value))) {
        return {
          amount: String(value),
          currency: orderCurrency(item)
        };
      }

      if (typeof value === "string" && /[₹$€£]/.test(value)) {
        return { formatted: value };
      }
    }

    return null;
  }

  function orderCurrency(item) {
    return (
      item?.currencyCode ||
      item?.priceCurrency ||
      "INR"
    );
  }

  function formatMoney(money) {
    if (!money) return "";
    if (money.formatted) return money.formatted;

    const amount = Number(money.amount);
    if (!Number.isFinite(amount)) return "";

    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: money.currency || "INR",
        maximumFractionDigits: 2
      }).format(amount);
    } catch {
      return `₹${amount.toLocaleString("en-IN")}`;
    }
  }

  function itemImageUrl(item) {
    return (
      item?.imageUrl ||
      item?.image?.url ||
      item?.image?.src ||
      item?.variant?.image?.url ||
      item?.product?.featuredImage?.url ||
      ""
    );
  }

  function itemTitle(item) {
    return normalizeText(
      item?.title ||
      item?.name ||
      item?.productTitle ||
      item?.product?.title ||
      "Gharyard product"
    );
  }

  function itemVariant(item) {
    const value = normalizeText(
      item?.variantTitle ||
      item?.variant?.title ||
      item?.variantName
    );

    return value && value.toLowerCase() !== "default title"
      ? value
      : "";
  }

  function renderProducts(order) {
    if (!productsCard || !productsList || !productsCount) return;

    const items = lineItemArray(order);

    productsList.replaceChildren();

    if (!items.length) {
      productsCard.hidden = true;
      productsCount.textContent = "";
      return;
    }

    productsCard.hidden = false;

    const totalQty = items.reduce((sum, item) => {
      const qty = Number(item?.quantity || 1);
      return sum + (Number.isFinite(qty) ? qty : 1);
    }, 0);

    productsCount.textContent =
      `${totalQty} item${totalQty === 1 ? "" : "s"}`;

    items.forEach(item => {
      const row = document.createElement("div");
      row.className = "got-product-row";

      const imageUrl = itemImageUrl(item);

      let visual;
      if (imageUrl) {
        visual = document.createElement("img");
        visual.className = "got-product-image";
        visual.src = imageUrl;
        visual.alt = itemTitle(item);
        visual.loading = "lazy";
        visual.decoding = "async";
      } else {
        visual = document.createElement("div");
        visual.className = "got-product-placeholder";
        visual.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 8 7-4 7 4-7 4-7-4Zm0 0v8l7 4 7-4V8M12 12v8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>';
      }

      const info = document.createElement("div");
      info.className = "got-product-info";

      const name = document.createElement("p");
      name.className = "got-product-name";
      name.textContent = itemTitle(item);

      info.appendChild(name);

      const variant = itemVariant(item);
      if (variant) {
        const variantEl = document.createElement("div");
        variantEl.className = "got-product-variant";
        variantEl.textContent = variant;
        info.appendChild(variantEl);
      }

      const qtyEl = document.createElement("div");
      qtyEl.className = "got-product-qty";
      qtyEl.textContent = `Quantity: ${Number(item?.quantity || 1)}`;
      info.appendChild(qtyEl);

      const price = document.createElement("div");
      price.className = "got-product-price";
      price.textContent = formatMoney(moneyValue(item));

      row.append(visual, info);

      if (price.textContent) {
        row.appendChild(price);
      }

      productsList.appendChild(row);
    });
  }

  function renderOrder(order) {
    if (!order) {
      throw new Error("Tracking service did not return order details.");
    }

    const shipment = bestShipment(order);
    const status = normalizedShipmentStatus(shipment, order);
    const latestEvent = newestShipmentEvent(shipment);
    const courierName = normalizeCourierName(shipment?.deliveryPartner);

    if (orderNumber) {
      orderNumber.textContent = normalizeText(order.name) || "";
    }

    setStatusPill(status);
    setWhatsAppLinks(order.name);

    if (orderedDate) {
      orderedDate.textContent = formatDate(order.createdAt) || "Not available";
    }

    if (estimatedDate) {
      if (shipment?.deliveredAt) {
        estimatedDate.textContent =
          `Delivered ${formatDate(shipment.deliveredAt)}`;
      } else {
        estimatedDate.textContent =
          formatDate(shipment?.estimatedDeliveryAt) ||
          "Not available yet";
      }
    }

    const newestTime =
      latestTimestamp(order, shipment, latestEvent);

    if (lastUpdated) {
      lastUpdated.textContent =
        formatDateTime(newestTime) ||
        "Not available yet";
    }

    setTimeline(order, shipment, status);

    const latestEventStatus =
      latestEvent?.status
        ? titleCaseStatus(latestEvent.status)
        : status;

    const latestEventTime =
      latestEvent?.happenedAt ||
      (status === "Delivered"
        ? shipment?.deliveredAt
        : status === "In Transit"
          ? shipment?.inTransitAt
          : status === "Shipped"
            ? shipment?.shippedAt
            : order?.createdAt);

    if (latestStatus) {
      latestStatus.textContent =
        latestEventStatus || status;
    }

    if (latestTime) {
      latestTime.textContent =
        formatDateTime(latestEventTime) ||
        formatDateTime(newestTime) ||
        "";
    }

    if (latestMessage) {
      latestMessage.textContent =
        normalizeText(latestEvent?.message) ||
        fallbackLatestMessage(status, courierName);
    }

    if (courier) {
      courier.textContent = courierName;
    }

    if (awb) {
      awb.textContent =
        normalizeText(shipment?.trackingNumber) ||
        "Not available yet";
    }

    if (currentStatusEl) {
      currentStatusEl.textContent = status;
    }

    if (lastUpdateEl) {
      const compactUpdate = latestEvent?.message
        ? normalizeText(latestEvent.message)
        : latestEventTime
          ? `${latestEventStatus} • ${formatDateTime(latestEventTime)}`
          : "Not available yet";

      lastUpdateEl.textContent = compactUpdate;
    }

    setCopyButton(normalizeText(shipment?.trackingNumber));
    setCourierLink(shipment?.trackingUrl, courierName);
    renderProducts(order);

    showResult();
  }

  async function requestTracking(orderId, contact) {
    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      REQUEST_TIMEOUT
    );

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ orderId, contact }),
        signal: controller.signal
      });

      let data;

      try {
        data = await response.json();
      } catch {
        throw new Error(
          "Tracking service returned an invalid response. Please try again."
        );
      }

      if (!response.ok || !data?.ok) {
        switch (data?.code) {
          case "NOT_VERIFIED":
            throw new Error(
              "We couldn't verify that order. Check the Order ID and registered phone number or email."
            );
          case "TOO_MANY_ATTEMPTS":
            throw new Error(
              "Too many tracking attempts. Please try again later."
            );
          case "CUSTOMER_DATA_ACCESS_REQUIRED":
            throw new Error(
              "Secure order verification is temporarily unavailable. Please contact Gharyard Support."
            );
          default:
            throw new Error(
              data?.error ||
              "Order tracking is temporarily unavailable. Please try again."
            );
        }
      }

      return data;
    } finally {
      clearTimeout(timeout);
    }
  }

  form?.addEventListener("submit", async event => {
    event.preventDefault();

    const orderId = normalizeText(orderInput?.value);
    const contact = normalizeText(contactInput?.value);

    clearError();

    if (!orderId || !contact) {
      showError(
        "Please enter both your Order ID and registered phone number or email."
      );
      return;
    }

    setLoading(true);

    try {
      const data = await requestTracking(orderId, contact);
      renderOrder(data.order);
    } catch (error) {
      console.error("[Gharyard Order Tracking]", error);

      const message =
        error?.name === "AbortError"
          ? "The tracking request took too long. Please try again."
          : error?.message ||
            "We couldn't retrieve your order right now. Please try again.";

      showError(message);
    } finally {
      setLoading(false);
    }
  });

  trackAnotherButton?.addEventListener("click", () => {
    hideResult();
    clearError();

    if (orderInput) orderInput.value = "";
    if (contactInput) contactInput.value = "";

    productsList?.replaceChildren();
    if (productsCard) productsCard.hidden = true;

    searchCard?.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "center"
    });

    if (canAutoFocus()) {
      window.setTimeout(() => {
        orderInput?.focus({ preventScroll: true });
      }, 400);
    }
  });
})();
